import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Crown, CheckCircle2, Send, Loader2, X, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";
const FOCUS = [
  { e: "👟", l: "Footwork" }, { e: "🎯", l: "Finishing" }, { e: "🛡️", l: "Defending" },
  { e: "⚡", l: "Speed" }, { e: "🧠", l: "Game IQ" }, { e: "🦶", l: "Weak Foot" },
];

// Row-level one-click "Session Complete" nudge (+points + Great Job email).
export function SessionCompleteButton({ token, player, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [focus, setFocus] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");
  const photoRef = useRef();
  const [busy, setBusy] = useState(false);
  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Photo too large (max 15MB)."); return; }
    if (preview) URL.revokeObjectURL(preview);
    setPhoto(f); setPreview(URL.createObjectURL(f));
  };
  const clearPhoto = () => { if (preview) URL.revokeObjectURL(preview); setPhoto(null); setPreview(""); if (photoRef.current) photoRef.current.value = ""; };
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("note", note); fd.append("focus", focus); fd.append("points", "50"); fd.append("send_email", "true");
      if (photo) fd.append("photo", photo, photo.name || "session.jpg");
      const { data } = await axios.post(`${API}/coach/players/${player.id}/session-complete`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      toast.success(`Session #${data.sessions} logged · +${data.points} pts${data.photo ? " · photo moment saved" : ""}`, { description: data.emailed ? `"Great Job" email sent to ${player.email}` : "No email sent (child profile or email off)" });
      setOpen(false); setNote(""); setFocus(""); clearPhoto(); onDone?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't log the session."); }
    finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button data-testid={`session-complete-${player.id}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm border border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10 transition-colors whitespace-nowrap">
        <CheckCircle2 size={12} /> Session ✓
      </button>
    );
  }
  return (
    <div data-testid={`session-form-${player.id}`} onClick={(e) => e.stopPropagation()} className="w-full space-y-2">
      <div data-testid={`session-focus-${player.id}`} className="flex flex-wrap items-center gap-1.5">
        <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 mr-1">Focus</span>
        {FOCUS.map((f) => (
          <button key={f.e} type="button" data-testid={`session-focus-${player.id}-${f.l.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setFocus(focus === f.e ? "" : f.e)} title={f.l} aria-pressed={focus === f.e}
            className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border transition-colors ${focus === f.e ? "border-[#D4AF37] bg-[#D4AF37]/15 text-white" : "border-white/10 bg-[#141414] text-white/60 hover:border-white/30"}`}>
            <span className="text-sm leading-none">{f.e}</span> <span className="hidden sm:inline font-bold uppercase tracking-wider">{f.l}</span>
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input ref={photoRef} data-testid={`session-photo-input-${player.id}`} type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhoto} />
        {preview ? (
          <button type="button" data-testid={`session-photo-preview-${player.id}`} onClick={clearPhoto} title="Remove photo" className="relative shrink-0 w-9 h-9 rounded-sm overflow-hidden border border-[#D4AF37]/70">
            <img loading="lazy" decoding="async" src={preview} alt="" className="w-full h-full object-cover" />
            <span className="absolute inset-0 bg-black/40 flex items-center justify-center text-white"><X size={12} /></span>
          </button>
        ) : (
          <button type="button" data-testid={`session-photo-btn-${player.id}`} onClick={() => photoRef.current?.click()} title="Snap a training photo" aria-label="Add photo"
            className="shrink-0 w-9 h-9 rounded-sm border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center transition-colors">
            <Camera size={15} />
          </button>
        )}
        <input data-testid={`session-note-${player.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Coach note (optional) — e.g. Left-foot Elastico looked sharp"
          className="flex-1 min-w-0 bg-[#141414] border border-white/15 rounded-sm px-2.5 py-2 text-xs text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#22C55E]" />
        <button data-testid={`session-send-${player.id}`} onClick={send} disabled={busy} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm bg-[#22C55E] text-black disabled:opacity-60 whitespace-nowrap">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {focus && <span>{focus}</span>} Send
        </button>
        <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white p-1.5" aria-label="Cancel"><X size={14} /></button>
      </div>
    </div>
  );
}

// VIP: log an in-person package → Elite access for N days.
export function VipPackageForm({ token, player, onGranted }) {
  const [pkg, setPkg] = useState("8-Session In-Person Pack");
  const [amount, setAmount] = useState("400");
  const [method, setMethod] = useState("cash");
  const [days, setDays] = useState("30");
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const active = player.vip_until && new Date(player.vip_until) > new Date();
  const grant = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/coach/players/${player.id}/vip`, { package: pkg, amount: Number(amount) || 0, method, days: Number(days) || 30 }, auth);
      toast.success("VIP Elite access unlocked", { description: `${player.name} is Elite until ${new Date(data.vip_until).toLocaleDateString()}` });
      onGranted?.(data.vip_until);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't grant VIP access."); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    try { await axios.delete(`${API}/coach/players/${player.id}/vip`, auth); toast.success("VIP access removed"); onGranted?.(null); }
    catch { toast.error("Couldn't revoke."); }
  };
  const input = "bg-[#141414] border border-white/10 rounded-md p-2 text-white text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#D4AF37]";
  return (
    <div data-testid="vip-package-form" className="mt-4 rounded-lg border p-4" style={{ borderColor: `${GOLD}55`, background: "linear-gradient(160deg, rgba(212,175,55,0.08), rgba(10,10,10,0.6))" }}>
      <div className="flex items-center gap-2 mb-1">
        <Crown size={14} style={{ color: GOLD }} />
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: GOLD }}>In-Person Training · VIP Elite Access</span>
        {active && <span data-testid="vip-active-badge" className="ml-auto text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: `${GOLD}22`, color: GOLD, border: `1px solid ${GOLD}55` }}>Elite until {new Date(player.vip_until).toLocaleDateString()}</span>}
      </div>
      <p className="text-[11px] text-white/50 mb-3">Log a paid in-person package — the player's Hub flips to Elite automatically for the chosen number of days (stacks on any active VIP window).</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <input data-testid="vip-package-input" value={pkg} onChange={(e) => setPkg(e.target.value)} className={`${input} col-span-2`} placeholder="Package" />
        <input data-testid="vip-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className={input} placeholder="$ Amount" inputMode="decimal" />
        <select data-testid="vip-method-select" value={method} onChange={(e) => setMethod(e.target.value)} className={input}>
          {["cash", "venmo", "zelle", "check", "card", "other"].map((m) => <option key={m} value={m}>{m[0].toUpperCase() + m.slice(1)}</option>)}
        </select>
        <div className="col-span-2 flex items-center gap-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">Elite for</label>
          <input data-testid="vip-days-input" value={days} onChange={(e) => setDays(e.target.value)} className={`${input} w-20`} inputMode="numeric" />
          <span className="text-[10px] uppercase tracking-widest text-white/40">days</span>
        </div>
        <button data-testid="vip-grant-btn" onClick={grant} disabled={busy} className="col-span-2 inline-flex items-center justify-center gap-2 text-black text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm disabled:opacity-60" style={{ background: GOLD }}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Crown size={13} />} Unlock Elite Access
        </button>
      </div>
      {active && <button data-testid="vip-revoke-btn" onClick={revoke} className="mt-2 text-[10px] uppercase tracking-wider text-white/40 hover:text-[#C41E24]">Remove VIP access</button>}
    </div>
  );
}
